/**
 * Manual food-entry panel — used by the FoodScanner "Manual" tab.
 *
 * Additive component. Zero changes to Nutrition.jsx or the existing
 * scanner tabs (scan/barcode/gallery). Behaviour spec:
 *   - Live autocomplete against /api/nutrition/food-search (Indian DB +
 *     admin-curated food_database collection), debounced 250ms.
 *   - Graceful miss: on zero hits, show "No match — enter it manually
 *     below" with the manual fields ALREADY visible (never a dead dropdown).
 *   - Live macro-preview ring: updates as the user types (kcal/protein/
 *     carbs/fat) so they see how the meal shapes the plate before logging.
 *   - Meal-type chips (Breakfast/Lunch/Dinner/Snacks) — pre-selected via
 *     the `initialMeal` prop passed from the ?meal= query.
 *   - On successful log after a miss, POSTs the user's macros to
 *     /api/nutrition/food-search/miss-context so the admin can see what
 *     real people typed and ate. Never auto-promoted to the DB.
 *   - No confetti anywhere. The +2 FitPoints toast is enough celebration
 *     for a daily action.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Utensils, Search as SearchIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../../services/api";

const MEALS = [
  { id: "breakfast", label: "Breakfast" },
  { id: "lunch", label: "Lunch" },
  { id: "dinner", label: "Dinner" },
  { id: "snacks", label: "Snacks" },
];

export default function ManualFoodEntry({ initialMeal, onLogged }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [gracefulMiss, setGracefulMiss] = useState(false);
  const [meal, setMeal] = useState(
    MEALS.find((m) => m.id === (initialMeal || "").toLowerCase()) ? initialMeal.toLowerCase() : "lunch"
  );

  // Manual override fields
  const [name, setName] = useState("");
  const [kcal, setKcal] = useState("");
  const [prot, setProt] = useState("");
  const [carb, setCarb] = useState("");
  const [fat, setFat] = useState("");
  const [portion, setPortion] = useState("1");
  const [submitting, setSubmitting] = useState(false);

  // For the miss-context write on submit.
  const missQueryRef = useRef("");

  // Debounced search
  useEffect(() => {
    const needle = q.trim();
    if (!needle) {
      setResults([]);
      setGracefulMiss(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(
          `/nutrition/food-search?q=${encodeURIComponent(needle)}&limit=8`
        );
        setResults(data.items || []);
        setGracefulMiss(!!data.graceful_miss && (data.items || []).length === 0);
        if (data.graceful_miss) missQueryRef.current = needle.toLowerCase();
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const pickResult = (item) => {
    setName(item.name || "");
    setKcal(String(Math.round(item.calories || 0)));
    setProt(String(item.protein_g ?? 0));
    setCarb(String(item.carbs_g ?? 0));
    setFat(String(item.fat_g ?? 0));
    setQ("");
    setResults([]);
    setGracefulMiss(false);
    missQueryRef.current = "";
  };

  const nKcal = Number(kcal) || 0;
  const nProt = Number(prot) || 0;
  const nCarb = Number(carb) || 0;
  const nFat = Number(fat) || 0;
  const nPortion = Math.max(0.25, Math.min(4, Number(portion) || 1));

  // Effective values after portion multiplier
  const eff = useMemo(() => ({
    kcal: Math.round(nKcal * nPortion),
    prot: +(nProt * nPortion).toFixed(1),
    carb: +(nCarb * nPortion).toFixed(1),
    fat: +(nFat * nPortion).toFixed(1),
  }), [nKcal, nProt, nCarb, nFat, nPortion]);

  const macroCalsFromMacros = eff.prot * 4 + eff.carb * 4 + eff.fat * 9;
  const cap = Math.max(eff.kcal, macroCalsFromMacros, 1);
  const pctProt = Math.min(1, (eff.prot * 4) / cap);
  const pctCarb = Math.min(1, (eff.carb * 4) / cap);
  const pctFat = Math.min(1, (eff.fat * 9) / cap);

  const canSubmit =
    !!name.trim() && nKcal > 0 && nProt >= 0 && nCarb >= 0 && nFat >= 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        meal_type: meal,
        name: name.trim(),
        calories: eff.kcal,
        protein_g: eff.prot,
        carbs_g: eff.carb,
        fat_g: eff.fat,
        portion_multiplier: nPortion,
        source: "manual",
      };
      const { data } = await apiClient.post("/nutrition/log", payload);

      // If the entry came AFTER a miss, feed the miss-context store.
      if (missQueryRef.current) {
        try {
          await apiClient.post("/nutrition/food-search/miss-context", {
            q: missQueryRef.current,
            name: payload.name,
            calories: payload.calories,
            protein_g: payload.protein_g,
            carbs_g: payload.carbs_g,
            fat_g: payload.fat_g,
          });
        } catch { /* non-fatal */ }
        missQueryRef.current = "";
      }

      const fp = data?.fitpoints_awarded || 0;
      toast.success(
        fp > 0 ? `${payload.name} logged · +${fp} FitPoints` : `${payload.name} logged`
      );
      // Clear entry for next log
      setName(""); setKcal(""); setProt(""); setCarb(""); setFat(""); setPortion("1");
      setQ(""); setResults([]); setGracefulMiss(false);
      onLogged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log meal");
    } finally {
      setSubmitting(false);
    }
  };

  const macroCalsMismatch =
    nKcal > 0 && macroCalsFromMacros > 0 && Math.abs(macroCalsFromMacros - nKcal) / nKcal > 0.35;

  return (
    <div
      className="bg-white rounded-[16px] p-5 border border-line2"
      data-testid="manual-tab"
      style={{ boxShadow: "var(--shadow-soft)" }}
    >
      <h3 className="font-display text-[18px]">Log manually</h3>
      <p className="text-[12px] text-ink-soft mt-1">
        Type a food to auto-fill macros, or enter your own values.
      </p>

      {/* Search box */}
      <div className="mt-3 relative">
        <SearchIcon
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary"
        />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search a food (e.g., paneer, dal, rice)…"
          data-testid="manual-search-input"
          className="w-full h-11 pl-9 pr-3 rounded-[12px] bg-canvas2 border border-line outline-none text-[14px]"
        />
        {(results.length > 0 || (q && gracefulMiss)) && (
          <div
            data-testid="manual-search-results"
            className="mt-2 border border-line2 rounded-[12px] overflow-hidden"
            style={{ background: "#FFF" }}
          >
            {results.map((it, idx) => (
              <button
                key={`${it.name}-${idx}`}
                type="button"
                onClick={() => pickResult(it)}
                data-testid={`manual-result-${idx}`}
                className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-canvas2 border-b border-line2 last:border-b-0"
              >
                <div className="min-w-0">
                  <div className="text-[13px] font-medium capitalize truncate">{it.name}</div>
                  <div className="text-[11px] text-ink-soft">
                    {Math.round(it.calories || 0)} kcal · P {it.protein_g}g · C {it.carbs_g}g · F {it.fat_g}g
                    {it.portion_description ? ` · ${it.portion_description}` : ""}
                  </div>
                </div>
                {it.source === "indian_db" && (
                  <span
                    className="text-[10px] font-bold uppercase tracking-[0.1em] px-2 py-1 rounded-pill shrink-0 ml-2"
                    style={{ background: "rgba(0,212,255,0.14)", color: "#00B0D5", borderRadius: 100 }}
                  >
                    Indian DB
                  </span>
                )}
              </button>
            ))}
            {gracefulMiss && (
              <div
                className="px-3 py-2.5 text-[12px] text-ink-soft flex items-center gap-2"
                data-testid="manual-graceful-miss"
                style={{ background: "rgba(255, 209, 102, 0.15)" }}
              >
                <Sparkles size={13} style={{ color: "#C08400" }} />
                No match — enter it manually below. We&apos;ll flag it for our team so the DB gets smarter.
              </div>
            )}
          </div>
        )}
        {searching && (
          <div className="text-[11px] text-ink-tertiary mt-1" data-testid="manual-searching">Searching…</div>
        )}
      </div>

      {/* Meal-type chips */}
      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-[0.1em] font-bold text-ink-tertiary mb-2">Meal</div>
        <div className="flex flex-wrap gap-1.5" data-testid="manual-meal-chips">
          {MEALS.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setMeal(m.id)}
              data-testid={`manual-meal-${m.id}`}
              className="px-3 h-8 rounded-pill text-[12px] font-medium"
              style={{
                background: meal === m.id ? "#0C0C0B" : "var(--canvas2, #F5F3EE)",
                color: meal === m.id ? "#FFFFFF" : "var(--text-primary, #1A1A1A)",
                borderRadius: 100,
                border: meal === m.id ? "1px solid #0C0C0B" : "1px solid var(--line2, #EEECE9)",
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Manual fields */}
      <div className="mt-4">
        <label className="text-[11px] uppercase tracking-[0.1em] font-bold text-ink-tertiary">Food name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Paneer bhurji"
          data-testid="manual-name-input"
          className="mt-1 w-full h-11 px-3 rounded-[12px] bg-canvas2 border border-line outline-none text-[14px]"
        />
      </div>

      <div className="grid grid-cols-2 gap-3 mt-3">
        <FieldNumber label="Calories (kcal)" testid="manual-kcal-input" value={kcal} onChange={setKcal} step="1" />
        <FieldNumber label="Portion (x1.0)" testid="manual-portion-input" value={portion} onChange={setPortion} step="0.25" />
        <FieldNumber label="Protein (g)" testid="manual-protein-input" value={prot} onChange={setProt} />
        <FieldNumber label="Carbs (g)" testid="manual-carbs-input" value={carb} onChange={setCarb} />
        <FieldNumber label="Fat (g)" testid="manual-fat-input" value={fat} onChange={setFat} />
      </div>

      {/* Live macro-preview ring — updates as you type */}
      <MacroPreview
        kcal={eff.kcal}
        prot={eff.prot}
        carb={eff.carb}
        fat={eff.fat}
        pctProt={pctProt}
        pctCarb={pctCarb}
        pctFat={pctFat}
      />

      {macroCalsMismatch && (
        <div
          className="mt-2 text-[11px] px-3 py-2 rounded-[10px]"
          data-testid="manual-macro-warning"
          style={{ background: "rgba(255, 209, 102, 0.2)", color: "#7A5B00" }}
        >
          Heads up: your macros work out to ~{Math.round(macroCalsFromMacros)} kcal but you entered {nKcal} kcal.
          One of them is off — worth a quick check before logging.
        </div>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={!canSubmit}
        data-testid="manual-log-btn"
        className="mt-4 w-full h-12 rounded-pill disabled:opacity-50 font-semibold"
        style={{ background: "var(--accent-blue)", color: "#000", borderRadius: 100 }}
      >
        {submitting ? "Logging…" : (
          <>
            <Utensils size={14} className="inline mr-1.5 -mt-0.5" />
            Log to {MEALS.find((x) => x.id === meal)?.label || "meal"}
          </>
        )}
      </button>
    </div>
  );
}

function FieldNumber({ label, value, onChange, testid, step = "1" }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.1em] font-bold text-ink-tertiary">{label}</label>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full h-11 px-3 rounded-[12px] bg-canvas2 border border-line outline-none text-[14px]"
      />
    </div>
  );
}

/** Compact ring that visualises the plate you're about to log. */
function MacroPreview({ kcal, prot, carb, fat, pctProt, pctCarb, pctFat }) {
  const size = 116;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const seg = (pct) => Math.max(0, pct) * c;

  return (
    <div
      className="mt-4 rounded-[14px] p-4 flex items-center gap-4"
      data-testid="manual-macro-preview"
      style={{ background: "var(--canvas2, #F5F3EE)", border: "1px dashed var(--line, #E2E0DC)" }}
    >
      <div style={{ width: size, height: size, position: "relative", flexShrink: 0 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={size / 2} cy={size / 2} r={r} stroke="#E2E0DC" strokeWidth={stroke} fill="none" />
          {/* Protein */}
          <circle
            cx={size / 2} cy={size / 2} r={r}
            stroke="#00FFB3" strokeWidth={stroke} fill="none"
            strokeDasharray={`${seg(pctProt)} ${c}`}
            strokeDashoffset={0}
            strokeLinecap="round"
          />
          {/* Carbs (offset by protein) */}
          <circle
            cx={size / 2} cy={size / 2} r={r}
            stroke="#00D4FF" strokeWidth={stroke} fill="none"
            strokeDasharray={`${seg(pctCarb)} ${c}`}
            strokeDashoffset={-seg(pctProt)}
            strokeLinecap="round"
          />
          {/* Fat (offset by protein+carbs) */}
          <circle
            cx={size / 2} cy={size / 2} r={r}
            stroke="#A09C97" strokeWidth={stroke} fill="none"
            strokeDasharray={`${seg(pctFat)} ${c}`}
            strokeDashoffset={-(seg(pctProt) + seg(pctCarb))}
            strokeLinecap="round"
          />
        </svg>
        <div
          className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
          data-testid="manual-macro-ring-total"
        >
          <div className="font-barlow-cond font-extrabold text-[24px] leading-none">{kcal}</div>
          <div className="text-[9px] uppercase tracking-[0.1em] text-ink-tertiary mt-1">kcal</div>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-ink-tertiary mb-1.5">Live preview</div>
        <MacroRow color="#00FFB3" label="Protein" value={`${prot} g`} />
        <MacroRow color="#00D4FF" label="Carbs" value={`${carb} g`} />
        <MacroRow color="#A09C97" label="Fat" value={`${fat} g`} />
      </div>
    </div>
  );
}

function MacroRow({ color, label, value }) {
  return (
    <div className="flex items-center gap-2 mt-1">
      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
      <span className="text-[12px] text-ink-soft flex-1">{label}</span>
      <span className="text-[12px] font-semibold text-ink-primary">{value}</span>
    </div>
  );
}
